import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { defaultKeymap } from '@codemirror/commands';
import { javascript } from '@codemirror/lang-javascript';
import { useGlobal } from '../contexts/GlobalContext';
import { useBuild } from '../contexts/BuildContext';
import { StudioV1 as Studio, StudioConfig } from 'algo-dsl';
import ShowJsonDialog from '../components/ShowJsonDialog';


function CodeEditor({ initialCode, clearResults, clearLogs, appendLog, setLogs, setResults }) {
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  const [isRunning, setIsRunning] = useState(false);
  const { apiKey, baseServiceUrl, defaultFarcasterUserId, defaultPolymarketWalletAddress, mergeCurrentContext } = useGlobal();
  const { feedBuilderApi } = useBuild();

  const [feedType, setFeedType] = useState('algorithm');
  const [feedInputs, setFeedInputs] = useState('user_id');
  const [sdkVersion, setSdkVersion] = useState('V1');
  const [farcasterFid, setFarcasterFid] = useState(defaultFarcasterUserId ?? '');
  const [polymarketWallet, setPolymarketWallet] = useState(defaultPolymarketWalletAddress ?? '');
  const [validationResult, setValidationResult] = useState(null);
  const [signedFeedDialogOpen, setSignedFeedDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [composeDialogOpen, setComposeDialogOpen] = useState(false);
  const [composeResult, setComposeResult] = useState(null);
  const [composeLoading, setComposeLoading] = useState(false);
  const clearValidationRef = useRef(null);
  clearValidationRef.current = () => setValidationResult(null);

  useEffect(() => {
    if (!containerRef.current) return;
    setValidationResult(null);

    const state = EditorState.create({
      doc: initialCode,
      extensions: [
        javascript(),
        keymap.of(defaultKeymap),
        EditorView.lineWrapping,
        EditorView.updateListener.of((update) => {
          if (update.docChanged && clearValidationRef.current) {
            clearValidationRef.current();
          }
          if (update.docChanged && mergeCurrentContext) {
            mergeCurrentContext({ code: update.state.doc.toString() });
          }
        }),
      ],
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;
    mergeCurrentContext?.({ code: initialCode });
    return () => {
      view.destroy();
      viewRef.current = null;
      mergeCurrentContext?.({ code: undefined });
    };
  }, [initialCode, mergeCurrentContext]);

  const handleRun = useCallback(() => {
    clearResults();
    clearLogs();
    const code = viewRef.current?.state?.doc?.toString() ?? '';
    if (!code.trim()) {
      appendLog('No code to run.');
      return;
    }
    const config = new StudioConfig({
      apiKey,
      commonUrl: baseServiceUrl,
      log: appendLog,
      show: setResults,
    });
    const studio = new Studio({ config });
    const search = studio.search();
    const root = feedType === 'minifeed' ? search : studio;
    setIsRunning(true);
    const done = () => {
      setIsRunning(false);
    };

    try {
      const AsyncFunction = (async () => {}).constructor;
      const rootParam = feedType === 'minifeed' ? 'search' : 'mbd';
      const params = feedInputs === 'user_id'
        ? `${rootParam}, farcasterFid, polymarketWallet`
        : rootParam;
      // eslint-disable-next-line no-new-func
      const run = new AsyncFunction(params, code);
      const result = feedInputs === 'user_id'
        ? run(root, farcasterFid, polymarketWallet)
        : run(root);
      const promise = result?.then ? result : Promise.resolve(result);
      promise
        .catch((err) => {
          appendLog(err?.message ?? String(err));
        })
        .finally(done);
    } catch (err) {
      appendLog(err?.message ?? String(err));
      done();
    }
  }, [apiKey, baseServiceUrl, clearResults, clearLogs, appendLog, setResults,
      feedType, feedInputs, farcasterFid, polymarketWallet]);

  const handleValidate = useCallback(async () => {
    const code = viewRef.current?.state?.doc?.toString() ?? '';
    if (!code.trim()) {
      setValidationResult({
        syntax_check: false,
        syntax_message: 'algorithm is empty',
        security_check: false,
        security_message: 'algorithm is empty',
      });
      return;
    }
    setIsRunning(true);
    setValidationResult(null);
    try {
      const result = await feedBuilderApi.validateFeed(code.trim(), sdkVersion, feedType, feedInputs);
      const r = result?.result ?? result;
      const syntaxOk = Boolean(r?.syntax_check);
      const securityOk = Boolean(r?.security_check);
      const signature = syntaxOk && securityOk ? r?.signature : undefined;
      setValidationResult({
        syntax_check: syntaxOk,
        syntax_message: r?.syntax_message ?? 'Unknown',
        security_check: securityOk,
        security_message: r?.security_message ?? 'Unknown',
        ...(signature && { signature }),
      });
    } catch (err) {
      setValidationResult({
        syntax_check: false,
        syntax_message: err?.message ?? 'Validation failed',
        security_check: false,
        security_message: err?.message ?? 'Validation failed',
      });
    } finally {
      setIsRunning(false);
    }
  }, [feedBuilderApi, sdkVersion, feedType, feedInputs]);

  const handleRunBackend = useCallback(async () => {
    const code = viewRef.current?.state?.doc?.toString() ?? '';
    const signature = validationResult?.signature;
    if (!code.trim() || !signature) {
      if (!code.trim()) appendLog('No code to run.');
      return;
    }
    clearResults();
    clearLogs();
    setIsRunning(true);
    try {
      const runRes = await feedBuilderApi.runFeed({
        sdk_version: sdkVersion,
        feed_type: feedType,
        feed_inputs: feedInputs,
        farcasterFid: feedInputs === 'user_id' ? (farcasterFid || null) : null,
        polymarketWallet: feedInputs === 'user_id' ? (polymarketWallet || null) : null,
        algorithm: code.trim(),
        signature,
        return_log: true,
      });
      const runResult = runRes?.result ?? runRes;
      if (runResult?.logs?.length) {
        setLogs(runResult.logs);
      }
      if (runResult?.feed) {
        setResults(runResult.feed);
      }
    } catch (err) {
      appendLog(err?.message ?? String(err));
    } finally {
      setIsRunning(false);
    }
  }, [feedBuilderApi, sdkVersion, feedType, feedInputs, farcasterFid, polymarketWallet, validationResult?.signature, clearResults, clearLogs, appendLog, setLogs, setResults]);

  const handleCompose = useCallback(async () => {
    const prompt = aiPrompt.trim();
    if (!prompt) return;
    setComposeLoading(true);
    setComposeResult(null);
    try {
      const res = await feedBuilderApi.composeFeed(prompt, sdkVersion, feedType, feedInputs);
      const r = res?.result ?? res;
      setComposeResult({
        algorithm: r?.algorithm ?? '',
        summary: r?.summary,
        error: res?.error,
      });
      setComposeDialogOpen(true);
    } catch (err) {
      setComposeResult({ algorithm: '', summary: null, error: err?.message ?? 'Compose failed' });
      setComposeDialogOpen(true);
    } finally {
      setComposeLoading(false);
    }
  }, [feedBuilderApi, aiPrompt, sdkVersion, feedType, feedInputs]);

  const handleComposeAccept = useCallback(() => {
    const algorithm = composeResult?.algorithm;
    if (algorithm && viewRef.current) {
      const view = viewRef.current;
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: algorithm },
      });
      mergeCurrentContext?.({ code: algorithm });
    }
    setComposeDialogOpen(false);
    setComposeResult(null);
    setAiPrompt('');
  }, [composeResult?.algorithm, mergeCurrentContext]);

  const handleComposeCancel = useCallback(() => {
    setComposeDialogOpen(false);
    setComposeResult(null);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <FormControl size="small" sx={{ maxWidth: 140 }}>
          <InputLabel>Feed Type</InputLabel>
          <Select
            value={feedType}
            label="Feed Type"
            onChange={(e) => setFeedType(e.target.value)}
          >
            <MenuItem value="algorithm">algorithm</MenuItem>
            <MenuItem value="minifeed">minifeed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ maxWidth: 140 }}>
          <InputLabel>Feed Inputs</InputLabel>
          <Select
            value={feedInputs}
            label="Feed Inputs"
            onChange={(e) => setFeedInputs(e.target.value)}
          >
            <MenuItem value="none">none</MenuItem>
            <MenuItem value="user_id">user_id</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ maxWidth: 120 }}>
          <InputLabel>DSL version</InputLabel>
          <Select
            value={sdkVersion}
            label="DSL version"
            onChange={(e) => setSdkVersion(e.target.value)}
          >
            <MenuItem value="V1">V1</MenuItem>
          </Select>
        </FormControl>
        {feedInputs === 'user_id' && (
          <>
            <TextField
              size="small"
              label="Farcaster User Id"
              value={farcasterFid}
              onChange={(e) => setFarcasterFid(e.target.value)}
              placeholder="Farcaster FID"
              fullWidth
            />
            <TextField
              size="small"
              label="Polymarket Wallet"
              value={polymarketWallet}
              onChange={(e) => setPolymarketWallet(e.target.value)}
              placeholder="0x…"
              fullWidth
            />
          </>
        )}
      </Box>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          size="small"
          label="AI prompt"
          placeholder="Describe the feed you want to generate…"
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleCompose();
            }
          }}
          fullWidth
          multiline
          minRows={3}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={composeLoading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
          onClick={handleCompose}
          disabled={composeLoading || !aiPrompt.trim()}
          sx={{ alignSelf: 'flex-start' }}
        >
          Compose
        </Button>
      </Box>
      <div
        ref={containerRef}
        style={{
          minHeight: 200,
          maxHeight: 800,
          overflow: 'auto',
          fontSize: 13,
        }}
      />
      <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={handleRun}
            disabled={isRunning}
          >
            Run in frontend
          </Button>
          {isRunning && <CircularProgress size={24} sx={{ ml: 'auto' }} />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleValidate}
            disabled={isRunning}
          >
            Validate
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={handleRunBackend}
            disabled={isRunning || !validationResult?.signature}
          >
            Run in backend
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CardGiftcardIcon />}
            onClick={() => setSignedFeedDialogOpen(true)}
            disabled={isRunning || !validationResult?.signature}
          >
            Signed feed
          </Button>
        </Box>
        <ShowJsonDialog
          open={signedFeedDialogOpen}
          onClose={() => setSignedFeedDialogOpen(false)}
          title="Signed feed"
          data={{
            feed_type: feedType,
            feed_inputs: feedInputs,
            sdk_version: sdkVersion,
            algorithm: viewRef.current?.state?.doc?.toString()?.trim(),
            signature: validationResult?.signature,
          }}
        />
        <Dialog open={composeDialogOpen} onClose={handleComposeCancel} maxWidth="md" fullWidth>
          <DialogTitle>AI Compose</DialogTitle>
          <DialogContent>
            {composeResult?.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {composeResult.error}
              </Alert>
            )}
            {composeResult?.summary && (
              <Box sx={{ mb: 2 }}>
                <Box component="span" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Summary: </Box>
                {composeResult.summary}
              </Box>
            )}
            {composeResult?.algorithm && (
              <Box
                component="pre"
                sx={{
                  backgroundColor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: 13,
                  fontFamily: 'monospace',
                  maxHeight: '50vh',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {composeResult.algorithm}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleComposeCancel}>Cancel</Button>
            <Button variant="contained" onClick={handleComposeAccept} disabled={!composeResult?.algorithm}>
              Accept
            </Button>
          </DialogActions>
        </Dialog>
        {validationResult && (
          <Stack direction="column" spacing={0.75}>
            <Alert
              icon={validationResult.syntax_check ? <CheckCircleIcon /> : <ErrorIcon />}
              severity={validationResult.syntax_check ? 'success' : 'error'}
              sx={{ py: 0.25, '& .MuiAlert-message': { py: 0.5 } }}
            >
              {validationResult.syntax_check ? 'Syntax check' : validationResult.syntax_message}
            </Alert>
            <Alert
              icon={validationResult.security_check ? <CheckCircleIcon /> : <ErrorIcon />}
              severity={validationResult.security_check ? 'success' : 'error'}
              sx={{ py: 0.25, '& .MuiAlert-message': { py: 0.5 } }}
            >
              {validationResult.security_check ? 'Security check' : validationResult.security_message}
            </Alert>
          </Stack>
        )}
      </Box>
    </div>
  );
}

export default CodeEditor;
